import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import MembershipFormLayout, { type MembershipTabId } from './components/MembershipFormLayout';
import MembershipInfoTab from './components/MembershipInfoTab';
import MembershipBrandMappingTab from './components/MembershipBrandMappingTab';
import MembershipVoucherSettingTab from './components/MembershipVoucherSettingTab';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { datetimeLocalToIsoUtc } from '../../utils/dateTime';
import {
  createMembership,
  extractMembershipCodeFromCreateResponse,
} from '../../utils/membership';
import { MembershipFormUtils } from '../../utils/membershipCommon';
import {
  DEFAULT_MEMBERSHIP_FORM_DATA,
  type MembershipFormData,
  type MembershipFormErrors,
  membershipNeedsVoucherTab,
  buildMembershipCreateRequest,
  MEMBERSHIP_CREATE_FIELD_MAPPING,
} from './components/membershipFormTypes';

const MembershipCreate: React.FC = () => {
  const navigate = useNavigate();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('membership-list');

  const [activeTab, setActiveTab] = useState<MembershipTabId>('info');
  const [membershipCode, setMembershipCode] = useState<string | null>(null);
  const [mappedBrandCount, setMappedBrandCount] = useState(0);

  const [formData, setFormData] = useState<MembershipFormData>(DEFAULT_MEMBERSHIP_FORM_DATA);
  const [errors, setErrors] = useState<MembershipFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  MembershipFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const projectInfo = MembershipFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  const projectCode = getProjectCode();

  const handleInfoSubmit = async (data: MembershipFormData) => {
    if (!projectCode) return;
    setSubmitError(null);
    try {
      setIsSubmitting(true);
      const membershipData = buildMembershipCreateRequest(data, datetimeLocalToIsoUtc);

      const result = await createMembership(projectCode, membershipData);
      if (result.success) {
        const code = extractMembershipCodeFromCreateResponse(result);
        if (code) {
          navigate(`/memberships/${encodeURIComponent(code)}/update?tab=brand`);
          return;
        } else {
          setSubmitError('멤버십 코드를 받지 못했습니다.');
        }
      } else {
        setSubmitError(result.message || '멤버십 생성에 실패했습니다.');
      }
    } catch (error: any) {
      if (error?.data?.errors?.error && Array.isArray(error.data.errors.error)) {
        const fieldErrors: MembershipFormErrors = {};
        const fieldMapping = MEMBERSHIP_CREATE_FIELD_MAPPING;
        const generalMessages: string[] = [];
        error.data.errors.error.forEach((err: any) => {
          if (!err?.message) return;
          const fieldKey = err.target ? fieldMapping[err.target] : undefined;
          if (fieldKey) {
            fieldErrors[fieldKey] = err.message;
          } else {
            generalMessages.push(err.message);
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        if (generalMessages.length > 0) {
          setSubmitError(generalMessages.join('\n'));
        } else if (Object.keys(fieldErrors).length === 0) {
          setSubmitError(CommonUtils.getNetworkErrorMessage(error));
        }
      } else {
        setSubmitError(CommonUtils.getNetworkErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInfoReset = () => {
    setFormData(DEFAULT_MEMBERSHIP_FORM_DATA);
    setErrors({});
    setSubmitError(null);
  };

  const handleBrandSaveSuccess = (newBrandCount?: number) => {
    if (typeof newBrandCount === 'number') setMappedBrandCount(newBrandCount);
    if (membershipNeedsVoucherTab(formData.rewardType)) {
      setActiveTab('voucher');
    } else {
      navigate('/memberships');
    }
  };

  const handleVoucherSaveSuccess = () => {
    navigate('/memberships');
  };

  const renderTabContent = () => {
    if (activeTab === 'info') {
      return (
        <MembershipInfoTab
          mode="create"
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleInfoSubmit}
          onReset={handleInfoReset}
          isSubmitting={isSubmitting}
          submitError={submitError}
          submitButtonText="저장 후 브랜드 매핑 진행"
          onBackClick={() => navigate('/memberships')}
        />
      );
    }
    if (activeTab === 'brand' && membershipCode && projectCode) {
      return (
        <MembershipBrandMappingTab
          projectCode={projectCode}
          membershipCode={membershipCode}
          membershipType={formData.type}
          rewardType={formData.rewardType}
          onSaveSuccess={handleBrandSaveSuccess}
        />
      );
    }
    if (activeTab === 'voucher' && membershipCode && projectCode) {
      return (
        <MembershipVoucherSettingTab
          projectCode={projectCode}
          membershipCode={membershipCode}
          stampSetCount={formData.stampSetCount}
          voucherExchangeMethod={formData.voucherExchangeMethod}
          onSaveSuccess={handleVoucherSaveSuccess}
        />
      );
    }
    return null;
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {projectDetail && (
        <ProjectSidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          projectDetail={projectDetail}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {projectDetail && (
          <Topbar
            title="멤버십 설정"
            subtitle={projectDetail.name}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isProfileDropdownOpen={isProfileDropdownOpen}
            setIsProfileDropdownOpen={setIsProfileDropdownOpen}
            showMobileLogo={true}
            projectDetail={projectDetail}
          />
        )}

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <MembershipFormLayout
              activeTab={activeTab}
              onTabChange={setActiveTab}
              membershipCode={membershipCode}
              rewardType={formData.rewardType}
              mappedBrandCount={mappedBrandCount}
              onBackClick={() => navigate('/memberships')}
            >
              {renderTabContent()}
            </MembershipFormLayout>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MembershipCreate;
